import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { SERVICE_BY_SLUG, CATEGORIES } from "../data/services";
import { Eyebrow, Reveal, scrollToId } from "../lib/ui";

const VARIANT = {
  boligadvokat: "editorial",
  tinglysning: "ledger",
  selskabsadvokat: "boardroom",
  "familie-og-arv": "warm",
};

const CAT_SIDE_IMG = {
  boligadvokat: "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&q=80",
  tinglysning: "https://images.unsplash.com/photo-1521791055366-0d553872125f?crop=entropy&cs=srgb&fm=jpg&q=80",
  selskabsadvokat: "https://images.unsplash.com/photo-1786640660909-a77524348b82?crop=entropy&cs=srgb&fm=jpg&q=80",
  "familie-og-arv": "https://images.unsplash.com/photo-1649083048337-4aeb6dda80bb?crop=entropy&cs=srgb&fm=jpg&q=80",
};

const CtaBand = ({ service, prefill }) => (
  <Reveal>
    <div className="flex flex-col items-start justify-between gap-6 rounded-3xl bg-brand-950 p-8 sm:flex-row sm:items-center sm:p-10">
      <div>
        <h3 className="text-xl font-semibold text-white sm:text-2xl">
          Klar til at komme videre med {service.title.toLowerCase()}?
        </h3>
        <p className="mt-2 max-w-lg text-sm leading-relaxed text-white/70 sm:text-base">
          Fortæl os kort om din sag — så kontakter vi dig og hjælper dig med at finde
          næste skridt. Det er uforpligtende.
        </p>
      </div>
      <button
        data-testid="service-bottom-cta"
        onClick={prefill}
        className="group inline-flex shrink-0 items-center gap-3 rounded-full bg-white py-3 pl-7 pr-2.5 text-sm font-semibold text-brand-950 transition-colors duration-200 hover:bg-brand-50"
      >
        Fortæl os om din sag
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-950 text-white transition-transform duration-200 group-hover:rotate-[-45deg]">
          <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
        </span>
      </button>
    </div>
  </Reveal>
);

const Others = ({ others, navigate }) => (
  <div className="mt-10">
    <p className="text-xs font-semibold tracking-[0.18em] text-slate-500">Andre ydelser</p>
    <ul className="mt-6 flex flex-wrap gap-3">
      {others.map((o) => (
        <li key={o.slug}>
          <button
            data-testid={`service-other-${o.slug}`}
            onClick={() => navigate(`/ydelser/${o.slug}`)}
            className="group inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 transition-all duration-200 hover:border-brand-600/50 hover:text-brand-600"
          >
            {o.title}
            <ArrowRight className="h-3.5 w-3.5 text-slate-300 transition-all group-hover:translate-x-0.5 group-hover:text-brand-600" />
          </button>
        </li>
      ))}
    </ul>
  </div>
);

/* --- Variant: EDITORIAL (Boligadvokat) — tekst + stort billede side om side --- */
const Editorial = ({ service }) => (
  <section className="py-16 sm:py-24">
    <div className="mx-auto grid max-w-[1440px] items-start gap-12 px-6 sm:px-8 lg:grid-cols-12 lg:px-12">
      <Reveal className="lg:col-span-7">
        <Eyebrow>Ydelsen</Eyebrow>
        <h2 className="mt-4 text-3xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-4xl">
          Hvad er {service.title.toLowerCase()}?
        </h2>
        <p className="mt-6 text-base leading-relaxed text-slate-600 sm:text-lg">{service.about}</p>
        <h3 className="mt-12 text-2xl font-semibold tracking-[-0.02em] text-slate-900">Hvad vi hjælper med</h3>
        <ul className="mt-6 grid gap-3">
          {service.help.map((item, i) => (
            <li key={i} data-testid={`service-help-item-${i + 1}`} className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 text-base leading-relaxed text-slate-700">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600">
                <Check className="h-3 w-3" strokeWidth={3} />
              </span>
              {item}
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.1} className="lg:col-span-5">
        <div className="overflow-hidden rounded-3xl lg:sticky lg:top-28">
          <img src={service.image} alt="" className="h-72 w-full object-cover lg:h-[480px]" loading="lazy" />
        </div>
      </Reveal>
    </div>
    <div className="mx-auto mt-20 max-w-[1440px] px-6 sm:px-8 lg:px-12">
      <Reveal>
        <Eyebrow>Sådan foregår det</Eyebrow>
        <h2 className="mt-4 text-3xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-4xl">Forløbet — trin for trin</h2>
      </Reveal>
      <div className="mt-12 grid gap-8 lg:grid-cols-3">
        {service.process.map(([title, text], i) => (
          <Reveal key={title} delay={i * 0.08}>
            <div data-testid={`service-step-${i + 1}`} className="border-t-2 border-brand-600 pt-6">
              <span className="text-5xl font-semibold tracking-tight text-brand-600/25">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-3 text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
              <p className="mt-2 text-base leading-relaxed text-slate-600">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* --- Variant: LEDGER (Tinglysning) — mørkt område + nummererede rækker --- */
const Ledger = ({ service }) => (
  <section className="py-16 sm:py-24">
    <div className="mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-12">
      <Reveal>
        <div className="rounded-3xl bg-brand-950 p-8 sm:p-12">
          <Eyebrow dark>Ydelsen</Eyebrow>
          <h2 className="mt-4 max-w-2xl text-3xl font-semibold tracking-[-0.02em] text-white sm:text-4xl">
            Hvad er {service.title.toLowerCase()}?
          </h2>
          <p className="mt-6 max-w-3xl text-base leading-relaxed text-white/75 sm:text-lg">{service.about}</p>
        </div>
      </Reveal>

      <div className="mt-16 grid gap-12 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <Eyebrow>Sådan foregår det</Eyebrow>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-4xl">Forløbet — trin for trin</h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Vi holder dig opdateret gennem hele forløbet — du ved altid, hvor i processen vi er.
            </p>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          {service.process.map(([title, text], i) => (
            <div key={title} data-testid={`service-step-${i + 1}`} className="flex gap-6 border-t border-slate-200 py-7 first:border-t-0 first:pt-0">
              <span className="shrink-0 font-mono text-sm font-semibold text-brand-600">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
                <p className="mt-2 text-base leading-relaxed text-slate-600">{text}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Reveal>
        <h2 className="mt-20 text-2xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-3xl">Hvad vi hjælper med</h2>
      </Reveal>
      <div className="mt-8 grid gap-3 sm:grid-cols-2">
        {service.help.map((item, i) => (
          <div key={i} data-testid={`service-help-item-${i + 1}`} className="flex h-full items-start gap-3 rounded-2xl bg-brand-50 p-5 text-base leading-relaxed text-slate-700">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-950 text-white">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            {item}
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --- Variant: BOARDROOM (Selskabsadvokat) — mørkt panel + store tal --- */
const Boardroom = ({ service }) => (
  <section className="py-16 sm:py-24">
    <div className="mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-12">
      <Reveal>
        <div className="grid gap-10 rounded-3xl bg-brand-950 p-8 sm:p-12 lg:grid-cols-2">
          <div>
            <Eyebrow dark>Ydelsen</Eyebrow>
            <h2 className="mt-4 text-3xl font-semibold tracking-[-0.02em] text-white sm:text-4xl">
              Hvad er {service.title.toLowerCase()}?
            </h2>
            <p className="mt-6 text-base leading-relaxed text-white/75 sm:text-lg">{service.about}</p>
          </div>
          <div>
            <h3 className="text-xs font-semibold tracking-[0.2em] text-white/60">Hvad vi hjælper med</h3>
            <ul className="mt-6 space-y-4">
              {service.help.map((item, i) => (
                <li key={i} data-testid={`service-help-item-${i + 1}`} className="flex items-start gap-3 text-base leading-relaxed text-white/90">
                  <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-950 text-white">
                    <Check className="h-2.5 w-2.5" strokeWidth={3} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>

      <Reveal>
        <h2 className="mt-20 text-3xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-4xl">Forløbet — trin for trin</h2>
      </Reveal>
      <div className="mt-12 grid gap-10 lg:grid-cols-4 lg:gap-8">
        {service.process.map(([title, text], i) => (
          <Reveal key={title} delay={i * 0.08}>
            <div data-testid={`service-step-${i + 1}`}>
              <span className="text-7xl font-semibold tracking-tight text-brand-600/15">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="mt-2 text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
              <p className="mt-2 text-base leading-relaxed text-slate-600">{text}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* --- Variant: WARM (Familie & arv) — bløde kort og fremhævet tekst --- */
const Warm = ({ service }) => (
  <section className="py-16 sm:py-24">
    <div className="mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-12">
      <Reveal>
        <div className="max-w-3xl border-l-4 border-brand-600 pl-6 sm:pl-8">
          <Eyebrow>Ydelsen</Eyebrow>
          <h2 className="mt-4 text-3xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-4xl">
            Hvad er {service.title.toLowerCase()}?
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-slate-700 sm:text-xl">{service.about}</p>
        </div>
      </Reveal>

      <Reveal>
        <h2 className="mt-20 text-2xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-3xl">Hvad vi hjælper med</h2>
      </Reveal>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {service.help.map((item, i) => (
          <div key={i} data-testid={`service-help-item-${i + 1}`} className="flex h-full items-start gap-3 rounded-2xl bg-brand-50/80 p-6 text-base leading-relaxed text-slate-700">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-950 text-white">
              <Check className="h-3 w-3" strokeWidth={3} />
            </span>
            {item}
          </div>
        ))}
      </div>

      <Reveal>
        <h2 className="mt-20 text-2xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-3xl">Forløbet — trin for trin</h2>
      </Reveal>
      <div className="mt-8 space-y-0">
        {service.process.map(([title, text], i) => (
          <div key={title} data-testid={`service-step-${i + 1}`} className="flex flex-col gap-3 border-t border-slate-200 py-7 sm:flex-row sm:items-start sm:gap-6">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-950 text-sm font-semibold text-white">
              {i + 1}
            </span>
            <div>
              <h3 className="text-lg font-semibold tracking-tight text-slate-900">{title}</h3>
              <p className="mt-1 max-w-3xl text-base leading-relaxed text-slate-600">{text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* --- FAQ (fælles, men forskellig baggrund pr. variant) --- */
const Faq = ({ service, tone = "white" }) => (
  <section className={`py-16 sm:py-20 ${tone === "tint" ? "border-t border-slate-200 bg-brand-50/50" : ""}`}>
    <div className="mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-12">
      <Reveal>
        <Eyebrow>FAQ</Eyebrow>
        <h2 className="mt-4 text-3xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-4xl">
          Ofte stillede spørgsmål
        </h2>
      </Reveal>
      <Reveal delay={0.05}>
        <Accordion type="single" collapsible className={`mt-10 rounded-3xl px-6 sm:px-8 ${tone === "tint" ? "border border-brand-100 bg-white" : "border border-slate-200 bg-white"}`}>
          {service.faq.map(([q, a], i) => (
            <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-item-${i + 1}`} className="border-slate-100 last:border-0">
              <AccordionTrigger className="py-5 text-left text-base font-semibold text-slate-900 hover:text-brand-600 hover:no-underline sm:text-lg">
                {q}
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-relaxed text-slate-600">
                {a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </Reveal>
    </div>
  </section>
);


const WHY_INTRO = {
  boligadvokat: "En bolighandel er indviklet — og konsekvenserne rækker langt ud i fremtiden. Derfor bør der være en advokat med hele vejen.",
  tinglysning: "Tinglysning er den formelle sikring af dit ejerskab og dine rettigheder. Derfor skal dokumenterne være helt korrekte — fra første tegn til tinglysning.",
  selskabsadvokat: "Selskabsdokumenter styrer hæftelse, beslutninger og exit. Derfor er det vigtigt, at de er skarpe fra dag ét.",
  "familie-og-arv": "Familiejuridiske dokumenter handler om dem, du holder af. Derfor skal de laves med omtanke — og være juridisk holdbare.",
};

const WhyItem = ({ i, title, text, tone }) => {
  if (tone === "dark")
    return (
      <div data-testid={`why-advokat-${i}`} className="border-t border-white/15 py-6">
        <span className="text-sm font-semibold text-brand-300">{String(i).padStart(2, "0")}</span>
        <h4 className="mt-2 text-lg font-semibold tracking-tight text-white">{title}</h4>
        <p className="mt-2 text-base leading-relaxed text-white/70">{text}</p>
      </div>
    );
  if (tone === "ghost")
    return (
      <div data-testid={`why-advokat-${i}`}>
        <span className="text-6xl font-semibold tracking-tight text-brand-600/15">{String(i).padStart(2, "0")}</span>
        <h4 className="mt-2 text-lg font-semibold tracking-tight text-slate-900">{title}</h4>
        <p className="mt-2 text-base leading-relaxed text-slate-600">{text}</p>
      </div>
    );
  if (tone === "soft")
    return (
      <div data-testid={`why-advokat-${i}`} className="flex h-full flex-col rounded-2xl bg-brand-50/80 p-6">
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-950 text-sm font-semibold text-white">{i}</span>
        <h4 className="mt-4 text-lg font-semibold tracking-tight text-slate-900">{title}</h4>
        <p className="mt-2 text-base leading-relaxed text-slate-600">{text}</p>
      </div>
    );
  return (
    <div data-testid={`why-advokat-${i}`} className="border-t-2 border-brand-600 pt-5">
      <span className="text-sm font-semibold text-brand-600">{String(i).padStart(2, "0")}</span>
      <h4 className="mt-2 text-lg font-semibold tracking-tight text-slate-900">{title}</h4>
      <p className="mt-2 text-base leading-relaxed text-slate-600">{text}</p>
    </div>
  );
};

const WhyCategory = ({ service, variant }) => {
  if (!service.why) return null;
  const tone = variant === "ledger" ? "dark" : variant === "boardroom" ? "ghost" : variant === "warm" ? "soft" : "light";
  const grid = tone === "ghost" ? "grid gap-8 lg:grid-cols-4" : "grid gap-4 sm:grid-cols-2 lg:gap-8";
  return (
    <div className="mt-16">
      {tone === "dark" ? (
        <div className="rounded-3xl bg-brand-950 p-8 sm:p-10 lg:p-12">
          <Eyebrow dark>Advokaten er dit sikkerhedsnet</Eyebrow>
          <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-[-0.02em] text-white sm:text-3xl">
            Hvorfor det er vigtigt at have advokat på
          </h3>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/70">{WHY_INTRO[service.slug]}</p>
          <div className="mt-8 grid gap-x-10 sm:grid-cols-2">
            {service.why.map(([title, text], i) => (
              <WhyItem key={title} i={i + 1} title={title} text={text} tone="dark" />
            ))}
          </div>
        </div>
      ) : (
        <Reveal>
          <Eyebrow>Advokaten er dit sikkerhedsnet</Eyebrow>
          <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-3xl">
            Hvorfor det er vigtigt at have advokat på
          </h3>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">{WHY_INTRO[service.slug]}</p>
          <div className={grid}>
            {service.why.map(([title, text], i) => (
              <WhyItem key={title} i={i + 1} title={title} text={text} tone={tone} />
            ))}
          </div>
        </Reveal>
      )}
    </div>
  );
};

const TipsBlock = ({ service, variant }) => {
  if (!service.tips) return null;
  return (
    <section className="border-t border-slate-200 py-16 sm:py-20">
      <div className="mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-12">
        <Reveal>
          <Eyebrow>Gode råd</Eyebrow>
          <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-3xl">
            Gode råd, inden du går i gang
          </h3>
        </Reveal>
        {variant === "ledger" ? (
          <div className="mt-10 rounded-3xl bg-brand-950 p-8 sm:p-10">
            <div>
              {service.tips.map(([title, text], i) => (
                <div key={title} className="flex gap-6 border-t border-white/10 py-5 first:border-t-0 first:pt-0">
                  <span className="shrink-0 text-sm font-semibold text-brand-300">{String(i + 1).padStart(2, "0")}</span>
                  <div className="flex-1 sm:grid sm:grid-cols-12 sm:gap-6">
                    <h4 className="text-base font-semibold tracking-tight text-white sm:col-span-4">{title}</h4>
                    <p className="mt-1 text-sm leading-relaxed text-white/70 sm:col-span-8 sm:mt-0">{text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : variant === "boardroom" ? (
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {service.tips.map(([title, text], i) => (
              <div key={title} className="border-t-2 border-brand-600 pt-5">
                <span className="text-4xl font-semibold tracking-tight text-brand-600/15">{String(i + 1).padStart(2, "0")}</span>
                <h4 className="mt-2 text-base font-semibold tracking-tight text-slate-900">{title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        ) : variant === "warm" ? (
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {service.tips.map(([title, text], i) => (
              <div key={title} className="rounded-2xl border border-brand-100 bg-white p-6">
                <h4 className="text-base font-semibold tracking-tight text-slate-900">{title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {service.tips.map(([title, text], i) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6">
                <h4 className="text-base font-semibold tracking-tight text-slate-900">{title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export const ServicePage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = SERVICE_BY_SLUG[slug];

  if (!service) {
    return (
      <main className="flex min-h-[70vh] flex-col items-center justify-center gap-4 pt-24 text-center">
        <p className="text-2xl font-bold text-slate-900">Ydelsen findes ikke</p>
        <button onClick={() => navigate("/ydelser")} className="rounded-full bg-brand-950 px-6 py-3 text-sm font-semibold text-white">
          Se alle ydelser
        </button>
      </main>
    );
  }

  const variant = service.isCategory ? VARIANT[service.slug] : VARIANT[service.category.slug];
  const others = service.isCategory
    ? CATEGORIES.filter((c) => c.slug !== slug).map((c) => ({ slug: c.slug, title: c.title }))
    : CATEGORIES.flatMap((c) => c.items)
        .filter((p) => p.slug !== slug)
        .slice(0, 4)
        .map((p) => ({ slug: p.slug, title: p.title }));

  const prefill = () => navigate("/kontakt", { state: { area: slug } });

  const CategoryProducts = () => {
    if (variant === "ledger") {
      return (
        <div className="space-y-6">
          {service.items.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.06}>
              <div
                role="link"
                tabIndex={0}
                data-testid={`service-product-${p.slug}`}
                onClick={() => navigate(`/ydelser/${p.slug}`)}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/ydelser/${p.slug}`)}
                className="group flex cursor-pointer flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition-all duration-300 hover:shadow-xl hover:shadow-slate-900/5 sm:flex-row"
              >
                <div className="h-44 sm:h-auto sm:w-64 shrink-0 overflow-hidden">
                  <img src={p.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                </div>
                <div className="flex flex-1 flex-col justify-center p-7">
                  <h3 className="text-xl font-semibold tracking-tight text-slate-900">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600">
                    Læs mere
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      );
    }
    if (variant === "boardroom") {
      return (
        <div className="rounded-3xl bg-brand-950 p-6 sm:p-10">
          <div className="grid gap-4 sm:grid-cols-3">
            {service.items.map((p) => (
              <div
                key={p.slug}
                role="link"
                tabIndex={0}
                data-testid={`service-product-${p.slug}`}
                onClick={() => navigate(`/ydelser/${p.slug}`)}
                onKeyDown={(e) => e.key === "Enter" && navigate(`/ydelser/${p.slug}`)}
                className="group cursor-pointer rounded-2xl border border-white/10 bg-white/5 p-6 transition-colors duration-300 hover:border-brand-500/60 hover:bg-white/10"
              >
                <h3 className="text-lg font-semibold tracking-tight text-white">{p.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/70">{p.short}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white">
                  Læs mere
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    const warm = variant === "warm";
    return (
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {service.items.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.06} className="h-full">
            <div
              role="link"
              tabIndex={0}
              data-testid={`service-product-${p.slug}`}
              onClick={() => navigate(`/ydelser/${p.slug}`)}
              onKeyDown={(e) => e.key === "Enter" && navigate(`/ydelser/${p.slug}`)}
              className={`group flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl transition-all duration-300 hover:-translate-y-1 ${
                warm
                  ? "border border-brand-100 bg-brand-50/60 hover:shadow-xl hover:shadow-brand-900/10"
                  : "border border-slate-200 bg-white hover:shadow-xl hover:shadow-slate-900/5"
              }`}
            >
              <div className="h-40 overflow-hidden">
                <img src={p.image} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="text-xl font-semibold tracking-tight text-slate-900">{p.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.short}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600">
                  Læs mere
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5" />
                </span>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    );
  };

  return (
    <main data-testid="service-page">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0" aria-hidden="true">
          <img src={service.image} alt="" className="h-full w-full object-cover object-center" />
          <div className={`absolute inset-0 ${variant === "warm" ? "bg-brand-950/60" : "bg-brand-950/70"}`} />
        </div>

        <div className="relative mx-auto max-w-[1440px] px-6 pb-20 pt-36 sm:px-8 sm:pb-24 sm:pt-44 lg:px-12 lg:pb-32 lg:pt-52">
          <button
            data-testid="service-back"
            onClick={() => navigate("/ydelser")}
            className="group mb-8 inline-flex items-center gap-2 text-sm font-medium text-white/70 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Alle ydelser
          </button>
          {!service.isCategory && (
            <p data-testid="service-breadcrumb" className="mb-4 text-xs font-semibold tracking-[0.2em] text-brand-300">
              {service.category.title}
            </p>
          )}
          <h1 data-testid="service-hero-title" className="max-w-3xl text-4xl font-semibold tracking-[-0.02em] text-white sm:text-5xl lg:text-6xl">
            {service.title}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg">
            {service.intro}
          </p>
          <button
            data-testid="service-cta"
            onClick={prefill}
            className="group mt-9 inline-flex items-center gap-3 rounded-full bg-brand-950 py-3 pl-7 pr-2.5 text-sm font-semibold text-white ring-1 ring-white/25 transition-colors duration-200 hover:bg-brand-900"
          >
            Fortæl os om din sag
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white text-brand-600 transition-transform duration-200 group-hover:rotate-[-45deg]">
              <ArrowRight className="h-3.5 w-3.5" strokeWidth={2.2} />
            </span>
          </button>
        </div>
      </section>

      {service.isCategory ? (
        <section className="py-16 sm:py-24">
          <div className="mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-12">
            <Reveal>
              <div className="grid items-start gap-10 lg:grid-cols-12">
                <div className="lg:col-span-7">
                  <Eyebrow>Området</Eyebrow>
                  <h2 className="mt-4 max-w-3xl text-3xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-4xl">
                    Hvad kan vi hjælpe dig med inden for {service.title.toLowerCase()}?
                  </h2>
                  <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">
                    {service.about}
                  </p>
                  {service.about2 && (
                    <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-600 sm:text-lg">
                      {service.about2}
                    </p>
                  )}
                </div>
                {CAT_SIDE_IMG[service.slug] && (
                  <div className="lg:col-span-5">
                    <img
                      src={CAT_SIDE_IMG[service.slug]}
                      alt=""
                      className="h-64 w-full rounded-3xl object-cover lg:h-[400px]"
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            </Reveal>

            <WhyCategory service={service} variant={variant} />
            <TipsBlock service={service} variant={variant} />

            {service.situations && (
              <div className="mt-16">
                <Reveal>
                  <Eyebrow>Typiske situationer</Eyebrow>
                  <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-3xl">
                    Vi har set det før — og ved, hvad der skal til
                  </h3>
                </Reveal>
                <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {service.situations.map(([title, text], i) => (
                    <Reveal key={title} delay={i * 0.05} className="h-full">
                      <div
                        data-testid={`situation-${i + 1}`}
                        className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-6"
                      >
                        <h4 className="text-base font-semibold tracking-tight text-slate-900">{title}</h4>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{text}</p>
                      </div>
                    </Reveal>
                  ))}
                </div>
              </div>
            )}

            {service.documents && (
              <div className="mt-16">
                <Reveal>
                  <Eyebrow>Dokumenterne</Eyebrow>
                  <h3 className="mt-4 max-w-2xl text-2xl font-semibold tracking-[-0.02em] text-slate-900 sm:text-3xl">
                    Det læser vi med juridiske øjne
                  </h3>
                </Reveal>
                <div className="mt-10 space-y-0">
                  {service.documents.map(([title, text], i) => (
                    <div
                      key={title}
                      data-testid={`document-row-${i + 1}`}
                      className="flex flex-col gap-2 border-t border-slate-200 py-6 sm:flex-row sm:items-start sm:gap-8"
                    >
                      <span className="shrink-0 font-medium text-brand-600">{String(i + 1).padStart(2, "0")}</span>
                      <div className="flex-1 sm:grid sm:grid-cols-12 sm:gap-6">
                        <h4 className="text-base font-semibold tracking-tight text-slate-900 sm:col-span-4">{title}</h4>
                        <p className="mt-1 text-sm leading-relaxed text-slate-600 sm:col-span-8 sm:mt-0">{text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-12">
              <CategoryProducts />
            </div>
            <div className="mt-16">
              <CtaBand service={service} prefill={prefill} />
            </div>
            <Others others={others} navigate={navigate} />
          </div>
        </section>
      ) : (
        <>
          {variant === "editorial" && <Editorial service={service} />}
          {variant === "ledger" && <Ledger service={service} />}
          {variant === "boardroom" && <Boardroom service={service} />}
          {variant === "warm" && <Warm service={service} />}

          <Faq service={service} tone={variant === "ledger" || variant === "warm" ? "tint" : "white"} />

          <TipsBlock service={service} variant={variant} />

          <section className="border-t border-slate-200 py-16 sm:py-20">
            <div className="mx-auto max-w-[1440px] px-6 sm:px-8 lg:px-12">
              <CtaBand service={service} prefill={prefill} />
              <Others others={others} navigate={navigate} />
            </div>
          </section>
        </>
      )}
    </main>
  );
};

export default ServicePage;